import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateScreen, updateScreenId } from './utils/state/screensSlice';
import { updateCanvasScreenId } from './utils/state/canvasSlice';
import { updateScreenIdInExperience } from './utils/state/experienceSlice';
import { ChevronDown, ChevronRight, Settings } from 'lucide-react';
import URLInputWithLibrary from './URLInputWithLibrary';
import FlowSelector from './FlowSelector';
import CompositeDesigner from './CompositeDesigner';
import { useReplicate } from './ReplicateContext';
import { ReplicatePropertyCheckbox } from './ReplicatePropertyCheckbox';
import TokenizedTextInput from './TokenizedTextInput';
import FontDropdown from './FontDropdown';

// Default values for all possible screen properties
const SCREEN_PROPERTY_DEFAULTS: { [key: string]: any } = {
    // Display properties
    'display.backgroundColor': '#ffffff',
    'display.backgroundMediaUrl': '',
    'display.foregroundColor': '#000000',
    'display.buttonColor': '#007AFF',
    'display.buttonTextColor': '#ffffff',
    'display.horizontalPadding': 20,
    'display.verticalPadding': 20,

    // Stage properties
    'stage.type': 'attract',
    'stage.mode': 'photo',
    'stage.flowId': '',
    'stage.shouldNavigateAfterCapture': true,
    'stage.postCaptureDestination': '',
    'stage.prompt': '',
    'stage.countDown': 3,
    'stage.processingMessage': 'Processing...',
    'stage.captureMessage': 'Smile!',
    'stage.dynamicTextIdentifier': '',
    'stage.captureOnMount': false,
    'stage.photoCount': 1,
    'stage.skipGifGeneration': false,
    'stage.gifDuration': undefined,
    'stage.gifCaptureDuration': undefined,
    'stage.fps': undefined,
    'stage.gifLoopCount': undefined,
    'stage.gifReverse': true,
    'stage.includeIndividualPhotos': true,
    'stage.addToSharedPhotos': true,
    'stage.replacePreviousCapture': false,
    'stage.timeout': undefined,
    'stage.videoProcessingFlowId': undefined,
    'stage.postCaptureAction': undefined,
    'stage.valueKey': undefined,
    'stage.navigateAfterCaptureDelay': 0,
    'stage.hasNoTimeout': false,
    'stage.takeFirstPhotoImmediately': false,

    // Processing properties
    'stage.processingType': 'composite',
    'stage.processingFlowId': '',
    'stage.inputs': [],
    'stage.expectedPhotoCount': 0,
    'stage.processingDynamicTextIdentifier': '',
    'stage.processingHeight': 1800,
    'stage.processingWidth': 600,
    'stage.overlayUrl': '',
    'stage.backgroundUrl': '',
    'stage.shouldNavigateAfterProcessing': true,
    'stage.postProcessingDestination': '',
    'stage.processingPhotoInstructions': [],
    'stage.autoPrint': false,
    'stage.addToSession': true,
    'stage.gifInputs': [],
    // Composite card border / attribution strip / session QR. Left undefined
    // so they stay absent in the exported JSON unless the designer sets them.
    'stage.cardBorderColor': undefined,
    'stage.cardBorderWidth': undefined,
    'stage.attributionStrip': undefined,
    'stage.sessionQr': undefined,
    // Processing-message label font (matches text element conventions). Left
    // undefined so the device defaults (system, 44, 600) apply unless set.
    'stage.processingMessageFontFace': undefined,
    'stage.processingMessageFontSize': undefined,
    'stage.processingMessageFontWeight': undefined,

    // Sharing properties
    'sharing.isEnabled': false,
    'sharing.buttonText': 'Share',
    'sharing.buttonColor': '#007AFF',
    'sharing.buttonTextColor': '#ffffff',
    'sharing.methods': [],
    'sharing.emailTemplate': '',
    'sharing.smsTemplate': '',
    'sharing.message': '',
    'sharing.hashtags': [],
    'sharing.url': '',
    'sharing.title': '',
    'sharing.description': '',

    // Metadata properties
    'metadata.title': '',
    'metadata.description': '',
    'metadata.tags': [],
    'metadata.category': '',
    'metadata.version': '1.0.0',
    'metadata.lastModified': new Date().toISOString(),
    'metadata.author': '',
    'metadata.notes': '',
};

interface ComprehensiveScreenEditorProps {
    screen: ScreenDefinition;
    onIdChange: (newId: string) => void;
}

interface PropertySection {
    title: string;
    icon: string;
    properties: string[];
}

const SCREEN_PROPERTY_SECTIONS: PropertySection[] = [
    {
        title: 'Display Properties',
        icon: '🎨',
        properties: [
            'display.backgroundColor',
            'display.backgroundMediaUrl',
            'display.foregroundColor',
            'display.buttonColor',
            'display.buttonTextColor',
            'display.horizontalPadding',
            'display.verticalPadding',
        ],
    },
    {
        title: 'Stage Configuration',
        icon: '🎭',
        properties: ['stage.type'],
    },
    {
        title: 'Capture Settings',
        icon: '📸',
        properties: [
            'stage.mode',
            'stage.flowId',
            'stage.shouldNavigateAfterCapture',
            'stage.postCaptureDestination',
            'stage.prompt',
            'stage.countDown',
            'stage.processingMessage',
            'stage.captureMessage',
            'stage.dynamicTextIdentifier',
            'stage.captureOnMount',
            'stage.photoCount',
            'stage.skipGifGeneration',
            'stage.gifDuration',
            'stage.gifCaptureDuration',
            'stage.fps',
            'stage.gifLoopCount',
            'stage.gifReverse',
            'stage.includeIndividualPhotos',
            'stage.addToSharedPhotos',
            'stage.replacePreviousCapture',
            'stage.timeout',
            'stage.videoProcessingFlowId',
            'stage.postCaptureAction',
            'stage.valueKey',
            'stage.navigateAfterCaptureDelay',
            'stage.hasNoTimeout',
            'stage.takeFirstPhotoImmediately',
        ],
    },
    {
        title: 'Processing Settings',
        icon: '⚙️',
        properties: [
            'stage.processingType',
            'stage.processingFlowId',
            'stage.gifInputs',
            'stage.gifDuration',
            'stage.fps',
            'stage.gifLoopCount',
            'stage.gifReverse',
            'stage.inputs',
            'stage.expectedPhotoCount',
            'stage.processingMessage',
            'stage.processingMessageFontFace',
            'stage.processingMessageFontSize',
            'stage.processingMessageFontWeight',
            'stage.processingDynamicTextIdentifier',
            'stage.processingHeight',
            'stage.processingWidth',
            'stage.overlayUrl',
            'stage.backgroundUrl',
            'stage.shouldNavigateAfterProcessing',
            'stage.postProcessingDestination',
            'stage.processingPhotoInstructions',
            'stage.cardBorderColor',
            'stage.cardBorderWidth',
            'stage.attributionStrip',
            'stage.sessionQr',
            'stage.autoPrint',
            'stage.addToSession',
        ],
    },
    {
        title: 'Navigation',
        icon: '🔄',
        properties: [
            'navigateDestination',
            'navigateAfterTimeout',
            'resetAfterTimeout',
        ],
    },
    {
        title: 'Sharing',
        icon: '📤',
        properties: [
            'sharing.isEnabled',
            'sharing.buttonText',
            'sharing.buttonColor',
            'sharing.buttonTextColor',
            'sharing.methods',
            'sharing.emailTemplate',
            'sharing.smsTemplate',
            'sharing.message',
            'sharing.hashtags',
            'sharing.url',
            'sharing.title',
            'sharing.description',
        ],
    },
    {
        title: 'Metadata',
        icon: '📝',
        properties: [
            'metadata.title',
            'metadata.description',
            'metadata.tags',
            'metadata.category',
            'metadata.version',
            'metadata.author',
            'metadata.notes',
        ],
    },
];

export default function ComprehensiveScreenEditor({
    screen,
    onIdChange,
}: ComprehensiveScreenEditorProps) {
    const {
        replicateMode,
        setSourceScreenId,
        setSourceElementId,
        setShowReplicateDialog,
        selectedProperties,
    } = useReplicate();
    const dispatch = useDispatch();
    const { activationId } = useParams();
    const [isCompositeDesignerOpen, setIsCompositeDesignerOpen] =
        useState(false);
    const screens = useSelector((state: any) => state.screens.present.entities);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['Display Properties', 'Stage Configuration']),
    );

    // Local state for screen ID to prevent immediate updates
    const [localScreenId, setLocalScreenId] = useState(screen.id);
    const [idUpdateTimeout, setIdUpdateTimeout] =
        useState<NodeJS.Timeout | null>(null);

    // Update local state when screen prop changes (e.g., when switching screens)
    useEffect(() => {
        setLocalScreenId(screen.id);
    }, [screen.id]);

    // Debounced screen ID update function
    const debouncedUpdateScreenId = useCallback(
        (newId: string) => {
            if (idUpdateTimeout) {
                clearTimeout(idUpdateTimeout);
            }

            // Capture the current screen ID when the debounce is triggered
            const currentScreenId = screen.id;
            const trimmedNewId = newId.trim();

            // Skip if the new ID is empty or the same as current
            if (trimmedNewId === '' || trimmedNewId === currentScreenId) {
                return;
            }

            const timeout = setTimeout(() => {
                // Update all states together to avoid timing issues
                dispatch(
                    updateScreenId({
                        id: currentScreenId,
                        newId: trimmedNewId,
                    }),
                );
                dispatch(
                    updateCanvasScreenId({
                        oldId: currentScreenId,
                        newId: trimmedNewId,
                    }),
                );

                // Update the experience's screen list if we have an activation ID
                if (activationId) {
                    dispatch(
                        updateScreenIdInExperience({
                            experienceId: +activationId,
                            oldId: currentScreenId,
                            newId: trimmedNewId,
                        }),
                    );
                }

                // Notify parent in next tick to ensure Redux updates are processed
                setTimeout(() => {
                    onIdChange(trimmedNewId);
                }, 0);
            }, 500); // 500ms delay

            setIdUpdateTimeout(timeout);
        },
        [dispatch, screen.id, onIdChange, idUpdateTimeout, activationId],
    );

    // Handle screen ID input changes
    const handleScreenIdChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newId = e.target.value;
            setLocalScreenId(newId);
            debouncedUpdateScreenId(newId);
        },
        [debouncedUpdateScreenId],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (idUpdateTimeout) {
                clearTimeout(idUpdateTimeout);
            }
        };
    }, [idUpdateTimeout]);

    const toggleSection = (sectionTitle: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionTitle)) {
            newExpanded.delete(sectionTitle);
        } else {
            newExpanded.add(sectionTitle);
        }
        setExpandedSections(newExpanded);
    };

    const updateScreenProperty = (propertyPath: string, value: any) => {
        if (propertyPath.startsWith('display.')) {
            const displayProp = propertyPath.replace('display.', '');
            dispatch(
                updateScreen({
                    id: screen.id,
                    data: {
                        displayProperties: {
                            ...screen.displayProperties,
                            [displayProp]: value,
                        },
                    },
                }),
            );
        } else if (propertyPath.startsWith('stage.')) {
            const stageProp = propertyPath.replace('stage.', '');
            if (stageProp === 'type') {
                dispatch(
                    updateScreen({
                        id: screen.id,
                        data: {
                            stage: {
                                ...screen.stage,
                                type: value,
                            },
                        },
                    }),
                );
            } else if (stageProp === 'processingType') {
                // The editor exposes the processing kind as `processingType`, but
                // it is persisted on the metadata as `type` (PEProcessingMetadata).
                dispatch(
                    updateScreen({
                        id: screen.id,
                        data: {
                            stage: {
                                ...screen.stage,
                                metadata: {
                                    ...screen.stage.metadata,
                                    type: value,
                                },
                            },
                        },
                    }),
                );
            } else {
                dispatch(
                    updateScreen({
                        id: screen.id,
                        data: {
                            stage: {
                                ...screen.stage,
                                metadata: {
                                    ...screen.stage.metadata,
                                    [stageProp]: value,
                                },
                            },
                        },
                    }),
                );
            }
        } else {
            dispatch(
                updateScreen({
                    id: screen.id,
                    data: {
                        [propertyPath]: value,
                    },
                }),
            );
        }
    };

    const getPropertyValue = (propertyPath: string): any => {
        let value: any;

        if (propertyPath.startsWith('display.')) {
            const displayProp = propertyPath.replace('display.', '');
            value = screen.displayProperties
                ? (screen.displayProperties as any)[displayProp]
                : undefined;
        } else if (propertyPath.startsWith('stage.')) {
            const stageProp = propertyPath.replace('stage.', '');
            if (stageProp === 'type') {
                value = screen.stage?.type;
            } else if (stageProp === 'processingType') {
                // Persisted on the metadata as `type` (see updateScreenProperty).
                value = screen.stage?.metadata
                    ? (screen.stage.metadata as any).type
                    : undefined;
            } else {
                value = screen.stage?.metadata
                    ? (screen.stage.metadata as any)[stageProp]
                    : undefined;
            }
        } else if (propertyPath.startsWith('sharing.')) {
            const sharingProp = propertyPath.replace('sharing.', '');
            const sharing = (screen as any).sharing;
            value = sharing ? sharing[sharingProp] : undefined;
        } else if (propertyPath.startsWith('metadata.')) {
            const metadataProp = propertyPath.replace('metadata.', '');
            value = screen.metadata
                ? (screen.metadata as any)[metadataProp]
                : undefined;
        } else {
            value = (screen as any)[propertyPath];
        }

        // Return default value if property is missing or undefined
        if (value === undefined || value === null) {
            return SCREEN_PROPERTY_DEFAULTS[propertyPath];
        }

        return value;
    };

    // Helper function to render property with replicate checkbox
    const renderPropertyWithReplicate = (
        propertyName: string,
        label: string,
        inputElement: React.ReactNode,
    ) => (
        <div key={propertyName} className="mb-3">
            <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700">
                    {label}
                </label>
                {replicateMode && (
                    <ReplicatePropertyCheckbox
                        property={propertyName}
                        label={label}
                    />
                )}
            </div>
            {inputElement}
        </div>
    );

    const renderPropertyField = (propertyName: string) => {
        const value = getPropertyValue(propertyName);

        const handleChange = (newValue: any) => {
            updateScreenProperty(propertyName, newValue);
        };

        // Skip properties that don't apply to this stage type
        if (!isPropertyApplicable(propertyName, screen.stage.type)) {
            return null;
        }

        switch (propertyName) {
            case 'stage.type':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Stage Type',
                    <select
                        value={value || 'attract'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="attract">Attract</option>
                        <option value="capture">Capture</option>
                        <option value="processing">Processing</option>
                        <option value="review">Review</option>
                        <option value="share">Share</option>
                        <option value="query">Query</option>
                    </select>,
                );

            case 'stage.mode':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Capture Mode
                        </label>
                        <select
                            value={value || 'photo'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="photo">Photo</option>
                            <option value="gif">GIF</option>
                            <option value="clicker">Clicker</option>
                            <option value="sequence">Sequence</option>
                        </select>
                    </div>
                );

            case 'stage.processingType':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Type
                        </label>
                        <select
                            value={value || 'composite'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="composite">Composite</option>
                            <option value="gif">GIF</option>
                        </select>
                    </div>
                );

            case 'stage.processingPhotoInstructions':
                const processingType = getPropertyValue('stage.processingType');
                if (processingType === 'composite') {
                    return (
                        <div key={propertyName} className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Photo Layout (
                                {Array.isArray(value) ? value.length : 0}{' '}
                                photos)
                            </label>
                            <button
                                onClick={() => setIsCompositeDesignerOpen(true)}
                                className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <Settings className="w-4 h-4" />
                                <span>Open Composite Designer</span>
                            </button>
                        </div>
                    );
                }
                return null;

            case 'stage.gifInputs': {
                const gifInputs = Array.isArray(value) ? value : [];
                const screenOptions = Object.values(screens).filter(
                    (s: any) => s.id !== screen.id,
                );
                const updateGifInputs = (next: any[]) => handleChange(next);
                const moveEntry = (from: number, to: number) => {
                    if (to < 0 || to >= gifInputs.length) return;
                    const next = [...gifInputs];
                    const [item] = next.splice(from, 1);
                    next.splice(to, 0, item);
                    updateGifInputs(next);
                };
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            GIF Frame Sources
                        </label>
                        <p className="mb-2 text-xs text-gray-500">
                            Ordered list of capture screens whose photos become
                            the GIF frames. Optionally run each source through a
                            processing flow. For a wigglegram, select an overall
                            Processing Flow Id containing Wigglegram. Source flows
                            can split lens views or apply photo filters.
                        </p>
                        {gifInputs.length === 0 && (
                            <p className="mb-2 text-xs italic text-gray-400">
                                No frame sources yet.
                            </p>
                        )}
                        {gifInputs.map((entry: any, index: number) => (
                            <div
                                key={index}
                                className="mb-2 rounded-md border border-gray-200 bg-white p-2"
                            >
                                <div className="mb-1 flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600">
                                        Frame {index + 1}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                moveEntry(index, index - 1)
                                            }
                                            disabled={index === 0}
                                            className="px-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30"
                                            title="Move up"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                moveEntry(index, index + 1)
                                            }
                                            disabled={
                                                index === gifInputs.length - 1
                                            }
                                            className="px-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30"
                                            title="Move down"
                                        >
                                            ↓
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                updateGifInputs(
                                                    gifInputs.filter(
                                                        (_: any, i: number) =>
                                                            i !== index,
                                                    ),
                                                )
                                            }
                                            className="px-1 text-xs text-red-500 hover:text-red-700"
                                            title="Remove"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                                <select
                                    value={entry.input ?? ''}
                                    onChange={(e) => {
                                        const next = [...gifInputs];
                                        next[index] = {
                                            ...next[index],
                                            input: e.target.value,
                                        };
                                        updateGifInputs(next);
                                    }}
                                    className="mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">
                                        Select capture screen...
                                    </option>
                                    {screenOptions.map((s: any) => (
                                        <option key={s.id} value={s.id}>
                                            {s.id}
                                        </option>
                                    ))}
                                </select>
                                <FlowSelector
                                    value={entry.flowId ?? undefined}
                                    onChange={(newValue) => {
                                        const fid = Array.isArray(newValue)
                                            ? newValue[0]
                                            : newValue;
                                        const next = [...gifInputs];
                                        next[index] = {
                                            ...next[index],
                                            flowId: fid ?? undefined,
                                        };
                                        updateGifInputs(next);
                                    }}
                                    multiple={false}
                                    placeholder="Optional processing flow..."
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                updateGifInputs([...gifInputs, { input: '' }])
                            }
                            className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            + Add Frame Source
                        </button>
                    </div>
                );
            }

            // Boolean inputs
            case 'stage.shouldNavigateAfterCapture':
            case 'stage.shouldNavigateAfterProcessing':
            case 'stage.captureOnMount':
            case 'stage.skipGifGeneration':
            case 'stage.includeIndividualPhotos':
            case 'stage.addToSharedPhotos':
            case 'stage.replacePreviousCapture':
            case 'stage.autoPrint':
            case 'stage.addToSession':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={value || false}
                                onChange={(e) => handleChange(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                {formatPropertyName(propertyName)}
                            </span>
                        </label>
                    </div>
                );

            // Flow selectors
            case 'stage.flowId':
            case 'stage.processingFlowId':
            case 'stage.videoProcessingFlowId':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <FlowSelector
                            value={value || undefined}
                            onChange={(newValue) =>
                                handleChange(
                                    Array.isArray(newValue)
                                        ? newValue[0]
                                        : newValue,
                                )
                            }
                            multiple={false}
                            placeholder="Select a processing flow..."
                        />
                    </div>
                );

            // Float inputs (allow decimals; clearing the field removes the value)
            case 'stage.gifDuration':
            case 'stage.gifCaptureDuration':
            case 'stage.fps':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="number"
                            value={value ?? ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                handleChange(
                                    next === ''
                                        ? undefined
                                        : parseFloat(next),
                                );
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={0}
                            step={0.1}
                            placeholder={
                                propertyName === 'stage.fps'
                                    ? 'Derived from GIF Duration'
                                    : ''
                            }
                        />
                        {propertyName === 'stage.fps' && (
                            <p className="mt-1 text-xs text-gray-500">
                                Optional. Frames per second for the GIF. Overrides
                                GIF Duration when set; each frame shows for 1 / fps
                                seconds.
                            </p>
                        )}
                    </div>
                );

            // GIF loop count (integer; clearing the field falls back to the default of 6)
            case 'stage.gifLoopCount':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="number"
                            value={value ?? ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                handleChange(
                                    next === '' ? undefined : parseInt(next),
                                );
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={1}
                            step={1}
                            placeholder="6"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. Number of times the GIF's frame sequence
                            plays. Defaults to 6. Lower it to produce a shorter
                            GIF, e.g. when a low fps would otherwise make the
                            GIF very long.
                        </p>
                    </div>
                );

            case 'stage.gifReverse':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={value ?? true}
                                onChange={(e) => handleChange(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Gif Reverse (Boomerang)
                            </span>
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                            When enabled (the default), each loop plays the
                            frames forward then backward. Disable to play the
                            frames forward only, shortening each loop.
                        </p>
                    </div>
                );

            // Composite card border color (color swatch + hex; clearing the
            // hex field removes the border so it stays absent unless set).
            case 'stage.cardBorderColor':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Card Border Color',
                    <div>
                        <div className="flex gap-2">
                            <input
                                type="color"
                                value={value || '#000000'}
                                onChange={(e) => handleChange(e.target.value)}
                                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                            />
                            <input
                                type="text"
                                value={value || ''}
                                onChange={(e) =>
                                    handleChange(e.target.value || undefined)
                                }
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="#000000"
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. Draws a hairline border around each placed
                            card on the composite. Leave blank for no border.
                        </p>
                    </div>,
                );

            // Composite card border width (float px; clearing the field falls
            // back to the device default of 6 when a border color is set).
            case 'stage.cardBorderWidth':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Card Border Width
                        </label>
                        <input
                            type="number"
                            value={value ?? ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                handleChange(
                                    next === '' ? undefined : parseFloat(next),
                                );
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={0}
                            step={1}
                            placeholder="6"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. Border thickness in pixels. Defaults to 6
                            when a border color is set.
                        </p>
                    </div>
                );

            // Processing-message label font. Mirrors text element conventions;
            // clearing a field falls back to the device default (system/44/600).
            case 'stage.processingMessageFontFace':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Message Font Face
                        </label>
                        <input
                            type="text"
                            value={value ?? ''}
                            onChange={(e) =>
                                handleChange(
                                    e.target.value === ''
                                        ? undefined
                                        : e.target.value,
                                )
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="system"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. PostScript font name (e.g.
                            Bungee-Regular), matching text element font faces.
                            Blank uses the system font.
                        </p>
                    </div>
                );

            case 'stage.processingMessageFontSize':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Message Font Size
                        </label>
                        <input
                            type="number"
                            value={value ?? ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                handleChange(
                                    next === '' ? undefined : parseFloat(next),
                                );
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={8}
                            step={1}
                            placeholder="44"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. Point size for the processing message.
                            Defaults to 44.
                        </p>
                    </div>
                );

            case 'stage.processingMessageFontWeight':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Message Font Weight
                        </label>
                        <input
                            type="number"
                            value={value ?? ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                handleChange(
                                    next === '' ? undefined : parseFloat(next),
                                );
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={100}
                            max={800}
                            step={100}
                            placeholder="600"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Optional. Numeric weight in hundreds (100-800),
                            matching text element weights. Defaults to 600
                            (semibold).
                        </p>
                    </div>
                );

            // Print-attribution strip (nested object). Stays absent until the
            // designer adds it; optional sub-fields clear back to undefined.
            case 'stage.attributionStrip': {
                const strip =
                    value && typeof value === 'object' && !Array.isArray(value)
                        ? value
                        : null;
                const updateStrip = (patch: { [key: string]: any }) => {
                    handleChange({ ...(strip ?? { source: '' }), ...patch });
                };
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Attribution Strip
                        </label>
                        <p className="mb-2 text-xs text-gray-500">
                            Optional. Draws a strip of resolved text (e.g. the
                            first player's email) along one edge of the
                            composite so a printed sheet can be matched back to
                            an order.
                        </p>
                        {strip === null ? (
                            <button
                                type="button"
                                onClick={() => handleChange({ source: '' })}
                                className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                + Add Attribution Strip
                            </button>
                        ) : (
                            <div className="space-y-2 rounded-md border border-gray-200 bg-white p-2">
                                <div className="flex items-center justify-end">
                                    <button
                                        type="button"
                                        onClick={() => handleChange(undefined)}
                                        className="px-1 text-xs text-red-500 hover:text-red-700"
                                        title="Remove attribution strip"
                                    >
                                        ✕ Remove
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Source
                                    </label>
                                    {/* eslint-disable no-template-curly-in-string */}
                                    <TokenizedTextInput
                                        value={strip.source ?? ''}
                                        onChange={(next) =>
                                            updateStrip({ source: next })
                                        }
                                        placeholder="${player1Email}"
                                    />
                                    {/* eslint-enable no-template-curly-in-string */}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Edge
                                    </label>
                                    <select
                                        value={strip.edge ?? 'left'}
                                        onChange={(e) =>
                                            updateStrip({ edge: e.target.value })
                                        }
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                        <option value="bottom">Bottom</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Width
                                    </label>
                                    <input
                                        type="number"
                                        value={strip.width ?? ''}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            updateStrip({
                                                width:
                                                    next === ''
                                                        ? undefined
                                                        : parseFloat(next),
                                            });
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min={0}
                                        step={1}
                                        placeholder="Auto (sized to font)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Font Face
                                    </label>
                                    <FontDropdown
                                        value={strip.fontFace ?? undefined}
                                        onChange={(fontName) =>
                                            updateStrip({ fontFace: fontName })
                                        }
                                        placeholder="Default font"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Font Size
                                    </label>
                                    <input
                                        type="number"
                                        value={strip.fontSize ?? ''}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            updateStrip({
                                                fontSize:
                                                    next === ''
                                                        ? undefined
                                                        : parseFloat(next),
                                            });
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min={0}
                                        step={1}
                                        placeholder="Auto"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Text Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={strip.color ?? '#000000'}
                                            onChange={(e) =>
                                                updateStrip({
                                                    color: e.target.value,
                                                })
                                            }
                                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={strip.color ?? ''}
                                            onChange={(e) =>
                                                updateStrip({
                                                    color:
                                                        e.target.value ||
                                                        undefined,
                                                })
                                            }
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Background Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={
                                                strip.backgroundColor ??
                                                '#ffffff'
                                            }
                                            onChange={(e) =>
                                                updateStrip({
                                                    backgroundColor:
                                                        e.target.value,
                                                })
                                            }
                                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={strip.backgroundColor ?? ''}
                                            onChange={(e) =>
                                                updateStrip({
                                                    backgroundColor:
                                                        e.target.value ||
                                                        undefined,
                                                })
                                            }
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }

            // Session QR stamp (nested object). Stays absent until the
            // designer adds it; optional sub-fields clear back to undefined.
            case 'stage.sessionQr': {
                const qr =
                    value && typeof value === 'object' && !Array.isArray(value)
                        ? value
                        : null;
                const updateQr = (patch: { [key: string]: any }) => {
                    handleChange({
                        ...(qr ?? { x: 0, y: 0, size: 300 }),
                        ...patch,
                    });
                };
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Session QR Code
                        </label>
                        <p className="mb-2 text-xs text-gray-500">
                            Optional. Bakes a QR code onto the composite that
                            links to the online gallery for the whole photo
                            session. Keep it at least ~200px on a 300 DPI print
                            (~0.7&quot;) with a margin so phones can scan it.
                        </p>
                        {qr === null ? (
                            <button
                                type="button"
                                onClick={() =>
                                    handleChange({ x: 0, y: 0, size: 300 })
                                }
                                className="w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                + Add Session QR Code
                            </button>
                        ) : (
                            <div className="space-y-2 rounded-md border border-gray-200 bg-white p-2">
                                <div className="flex items-center justify-end">
                                    <button
                                        type="button"
                                        onClick={() => handleChange(undefined)}
                                        className="px-1 text-xs text-red-500 hover:text-red-700"
                                        title="Remove session QR code"
                                    >
                                        ✕ Remove
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Data
                                    </label>
                                    {/* eslint-disable no-template-curly-in-string */}
                                    <TokenizedTextInput
                                        value={qr.data ?? ''}
                                        onChange={(next) =>
                                            updateQr({
                                                data: next || undefined,
                                            })
                                        }
                                        placeholder="${sessionUrl}"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Blank encodes the session gallery URL
                                        ({'${sessionUrl}'}).
                                    </p>
                                    {/* eslint-enable no-template-curly-in-string */}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {(['x', 'y', 'size'] as const).map(
                                        (field) => (
                                            <div key={field}>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    {field === 'size'
                                                        ? 'Size'
                                                        : field.toUpperCase()}
                                                </label>
                                                <input
                                                    type="number"
                                                    value={qr[field] ?? ''}
                                                    onChange={(e) => {
                                                        const next =
                                                            e.target.value;
                                                        updateQr({
                                                            [field]:
                                                                next === ''
                                                                    ? 0
                                                                    : parseFloat(
                                                                          next,
                                                                      ),
                                                        });
                                                    }}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    min={0}
                                                    step={1}
                                                />
                                            </div>
                                        ),
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Margin (Quiet Zone)
                                    </label>
                                    <input
                                        type="number"
                                        value={qr.margin ?? ''}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            updateQr({
                                                margin:
                                                    next === ''
                                                        ? undefined
                                                        : parseFloat(next),
                                            });
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min={0}
                                        step={1}
                                        placeholder="0"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Band around the code filled with the
                                        background color, in pixels. Scanners
                                        need one when the code sits on busy
                                        artwork.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Foreground Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={
                                                qr.foregroundColor ?? '#000000'
                                            }
                                            onChange={(e) =>
                                                updateQr({
                                                    foregroundColor:
                                                        e.target.value,
                                                })
                                            }
                                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={qr.foregroundColor ?? ''}
                                            onChange={(e) =>
                                                updateQr({
                                                    foregroundColor:
                                                        e.target.value ||
                                                        undefined,
                                                })
                                            }
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Background Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={
                                                qr.backgroundColor?.startsWith(
                                                    '#',
                                                )
                                                    ? qr.backgroundColor
                                                    : '#ffffff'
                                            }
                                            onChange={(e) =>
                                                updateQr({
                                                    backgroundColor:
                                                        e.target.value,
                                                })
                                            }
                                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={qr.backgroundColor ?? ''}
                                            onChange={(e) =>
                                                updateQr({
                                                    backgroundColor:
                                                        e.target.value ||
                                                        undefined,
                                                })
                                            }
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder='#FFFFFF or "transparent"'
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Hex, or &quot;transparent&quot; to let
                                        the artwork show through the light
                                        modules. Keep strong contrast with the
                                        foreground or the code won&apos;t scan.
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Error Correction
                                    </label>
                                    <select
                                        value={qr.correctionLevel ?? 'M'}
                                        onChange={(e) =>
                                            updateQr({
                                                correctionLevel:
                                                    e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="L">L (7%)</option>
                                        <option value="M">M (15%)</option>
                                        <option value="Q">Q (25%)</option>
                                        <option value="H">H (30%)</option>
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Higher levels survive more overprint
                                        and damage at the cost of denser
                                        modules.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }

            // Integer number inputs
            case 'stage.countDown':
            case 'stage.photoCount':
            case 'stage.expectedPhotoCount':
            case 'stage.processingHeight':
            case 'stage.processingWidth':
            case 'navigateAfterTimeout':
            case 'resetAfterTimeout':
            case 'display.horizontalPadding':
            case 'display.verticalPadding':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="number"
                            value={value || 0}
                            onChange={(e) =>
                                handleChange(parseInt(e.target.value) || 0)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min={0}
                        />
                    </div>
                );

            // Color inputs
            case 'display.backgroundColor':
            case 'display.foregroundColor':
            case 'display.buttonColor':
            case 'display.buttonTextColor':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <div className="flex gap-2">
                        <input
                            type="color"
                            value={value || '#000000'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="#000000"
                        />
                    </div>,
                );

            // URL inputs with library shortcut
            case 'display.backgroundMediaUrl':
            case 'stage.overlayUrl':
            case 'stage.backgroundUrl':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <URLInputWithLibrary
                        value={value}
                        onChange={handleChange}
                        placeholder={getPropertyPlaceholder(propertyName)}
                        label={formatPropertyName(propertyName)}
                    />,
                );

            case 'stage.postCaptureDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Post Capture Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            case 'stage.postProcessingDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Post Processing Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            case 'navigateDestination':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Navigate Destination
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) =>
                                handleChange(e.target.value || undefined)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                Select destination screen...
                            </option>
                            {Object.values(screens)
                                .filter(
                                    (screenOption: any) =>
                                        screenOption.id !== screen.id,
                                ) // Don't show current screen
                                .map((screenOption: any) => (
                                    <option
                                        key={screenOption.id}
                                        value={screenOption.id}
                                    >
                                        {screenOption.id}
                                    </option>
                                ))}
                        </select>
                    </div>
                );

            // Token-aware text inputs (these strings are run through the
            // booth's ${...} token replacement at render time).
            case 'stage.prompt':
            case 'stage.processingMessage':
            case 'stage.captureMessage':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <TokenizedTextInput
                            value={value || ''}
                            onChange={handleChange}
                            placeholder={getPropertyPlaceholder(propertyName)}
                        />
                    </div>
                );

            // Text inputs
            case 'stage.dynamicTextIdentifier':
            case 'stage.processingDynamicTextIdentifier':
            case 'stage.inputs':
            default:
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {formatPropertyName(propertyName)}
                        </label>
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={getPropertyPlaceholder(propertyName)}
                        />
                    </div>
                );
        }
    };

    const isSectionApplicable = (
        sectionTitle: string,
        stageType: string,
    ): boolean => {
        switch (sectionTitle) {
            case 'Capture Settings':
                return stageType === 'capture';
            case 'Processing Settings':
                return stageType === 'processing';
            default:
                return true; // All other sections are always shown
        }
    };

    const isPropertyApplicable = (
        propertyName: string,
        stageType: string,
    ): boolean => {
        // Display properties are always applicable
        if (propertyName.startsWith('display.')) {
            return true;
        }

        // Stage type is always applicable
        if (propertyName === 'stage.type') {
            return true;
        }

        // Metadata properties are always applicable
        if (propertyName.startsWith('metadata.')) {
            return true;
        }

        // Navigation properties are always applicable
        if (
            [
                'navigateDestination',
                'navigateAfterTimeout',
                'resetAfterTimeout',
            ].includes(propertyName)
        ) {
            return true;
        }

        // Sharing properties are always applicable
        if (propertyName.startsWith('sharing.')) {
            return true;
        }

        // GIF pacing/looping fields are shared by gif/sequence capture screens
        // and gif processing screens.
        if (
            propertyName === 'stage.gifDuration' ||
            propertyName === 'stage.fps' ||
            propertyName === 'stage.gifLoopCount' ||
            propertyName === 'stage.gifReverse'
        ) {
            if (stageType === 'capture') return true;
            return (
                stageType === 'processing' &&
                getPropertyValue('stage.processingType') === 'gif'
            );
        }

        // Capture-specific properties only show for capture screens
        const captureProperties = [
            'stage.mode',
            'stage.flowId',
            'stage.shouldNavigateAfterCapture',
            'stage.postCaptureDestination',
            'stage.prompt',
            'stage.countDown',
            'stage.processingMessage',
            'stage.captureMessage',
            'stage.dynamicTextIdentifier',
            'stage.captureOnMount',
            'stage.photoCount',
            'stage.skipGifGeneration',
            'stage.gifCaptureDuration',
            'stage.includeIndividualPhotos',
            'stage.addToSharedPhotos',
            'stage.replacePreviousCapture',
            'stage.timeout',
            'stage.videoProcessingFlowId',
            'stage.postCaptureAction',
            'stage.valueKey',
            'stage.navigateAfterCaptureDelay',
            'stage.hasNoTimeout',
            'stage.takeFirstPhotoImmediately',
        ];
        if (captureProperties.includes(propertyName)) {
            return stageType === 'capture';
        }

        // GIF-only processing field
        if (propertyName === 'stage.gifInputs') {
            return (
                stageType === 'processing' &&
                getPropertyValue('stage.processingType') === 'gif'
            );
        }

        // Composite-only processing fields
        const compositeOnlyProcessing = [
            'stage.inputs',
            'stage.expectedPhotoCount',
            'stage.processingHeight',
            'stage.processingWidth',
            'stage.overlayUrl',
            'stage.backgroundUrl',
            'stage.processingPhotoInstructions',
            'stage.cardBorderColor',
            'stage.cardBorderWidth',
            'stage.attributionStrip',
            'stage.sessionQr',
        ];
        if (compositeOnlyProcessing.includes(propertyName)) {
            return (
                stageType === 'processing' &&
                getPropertyValue('stage.processingType') === 'composite'
            );
        }

        // Processing-specific properties shared by all processing types
        const processingProperties = [
            'stage.processingType',
            'stage.processingFlowId',
            'stage.processingDynamicTextIdentifier',
            'stage.shouldNavigateAfterProcessing',
            'stage.postProcessingDestination',
            'stage.autoPrint',
            'stage.addToSession',
            'stage.processingMessageFontFace',
            'stage.processingMessageFontSize',
            'stage.processingMessageFontWeight',
        ];
        if (processingProperties.includes(propertyName)) {
            return stageType === 'processing';
        }

        // Show all other properties by default
        return true;
    };

    const formatPropertyName = (propertyName: string): string => {
        // Remove the prefix (display., stage.) for display
        const cleanName = propertyName.replace(/^(display|stage)\./, '');
        return cleanName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const getPropertyPlaceholder = (propertyName: string): string => {
        const placeholders: { [key: string]: string } = {
            backgroundMediaUrl: 'https://example.com/background.jpg',
            postCaptureDestination: 'screen-id',
            postProcessingDestination: 'screen-id',
            navigateDestination: 'screen-id',
            prompt: 'Get ready for your photo!',
            processingMessage: 'Processing your photo...',
            captureMessage: 'Smile!',
            dynamicTextIdentifier: 'capture-message',
            processingDynamicTextIdentifier: 'processing-message',
            overlayUrl: 'https://example.com/overlay.png',
            backgroundUrl: 'https://example.com/background.jpg',
            inputs: 'screen-id-1,screen-id-2',
        };
        return placeholders[propertyName] || '';
    };

    return (
        <div className="space-y-4">
            {/* Screen ID Field */}
            <div className="p-3 bg-gray-50 rounded-lg border">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Screen ID
                </label>
                <input
                    type="text"
                    value={localScreenId}
                    onChange={handleScreenIdChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Screen Stats */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                        <div className="text-lg font-semibold text-green-700">
                            {screen.elements.length}
                        </div>
                        <div className="text-xs text-green-600">Elements</div>
                    </div>
                    <div>
                        <div className="text-lg font-semibold text-green-700">
                            {screen.stage.type}
                        </div>
                        <div className="text-xs text-green-600">Stage</div>
                    </div>
                </div>
            </div>

            {/* Replicate Mode Controls */}
            {replicateMode && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-sm text-gray-700">
                        Replicate screen properties to other screens
                    </div>

                    <button
                        onClick={() => {
                            setSourceScreenId(screen.id);
                            setSourceElementId(null); // Clear element since we're replicating screen
                            setShowReplicateDialog(true);
                        }}
                        disabled={selectedProperties.length === 0}
                        className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Replicate ({selectedProperties.length})
                    </button>
                </div>
            )}
            {/* Property Sections */}
            {SCREEN_PROPERTY_SECTIONS.map((section) => {
                // Check if the entire section should be shown for this stage type
                if (!isSectionApplicable(section.title, screen.stage.type)) {
                    return null;
                }

                const applicableProperties = section.properties.filter((prop) =>
                    isPropertyApplicable(prop, screen.stage.type),
                );

                if (applicableProperties.length === 0) return null;

                const isExpanded = expandedSections.has(section.title);

                return (
                    <div
                        key={section.title}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                        <button
                            onClick={() => toggleSection(section.title)}
                            className="w-full max-w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors overflow-hidden"
                        >
                            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                                <span className="text-sm flex-shrink-0">
                                    {section.icon}
                                </span>
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {section.title}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                    ({applicableProperties.length})
                                </span>
                            </div>
                            <div className="flex-shrink-0 w-8 h-6 flex items-center justify-center pr-2">
                                {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                            </div>
                        </button>

                        {isExpanded && (
                            <div className="p-3 border-t border-gray-200 bg-gray-50">
                                {applicableProperties.map((propertyName) =>
                                    renderPropertyField(propertyName),
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Composite Designer */}
            {isCompositeDesignerOpen && (
                <CompositeDesigner
                    isOpen={isCompositeDesignerOpen}
                    onClose={() => setIsCompositeDesignerOpen(false)}
                    width={getPropertyValue('stage.processingWidth') || 600}
                    height={getPropertyValue('stage.processingHeight') || 1800}
                    backgroundUrl={
                        getPropertyValue('stage.backgroundUrl') || ''
                    }
                    overlayUrl={getPropertyValue('stage.overlayUrl') || ''}
                    photoInstructions={
                        getPropertyValue('stage.processingPhotoInstructions') ||
                        []
                    }
                    expectedPhotoCount={
                        getPropertyValue('stage.expectedPhotoCount') || 0
                    }
                    usePreprocessedInputs={
                        getPropertyValue('stage.usePreprocessedInputs') || false
                    }
                    onSave={(
                        instructions,
                        expectedCount,
                        backgroundUrl,
                        overlayUrl,
                        usePreprocessedInputs,
                    ) => {
                        // Create a deep copy of the screen to avoid mutation
                        const updatedScreen = JSON.parse(
                            JSON.stringify(screen),
                        );

                        // Ensure stage and metadata exist
                        if (!updatedScreen.stage) {
                            updatedScreen.stage = {
                                type: 'processing',
                                metadata: {},
                            };
                        }
                        if (!updatedScreen.stage.metadata) {
                            updatedScreen.stage.metadata = {};
                        }

                        // Update the processing photo instructions and related properties
                        updatedScreen.stage.metadata.processingPhotoInstructions =
                            instructions;
                        updatedScreen.stage.metadata.expectedPhotoCount =
                            expectedCount;

                        if (backgroundUrl !== undefined) {
                            updatedScreen.stage.metadata.backgroundUrl =
                                backgroundUrl;
                        }
                        if (overlayUrl !== undefined) {
                            updatedScreen.stage.metadata.overlayUrl =
                                overlayUrl;
                        }
                        if (usePreprocessedInputs !== undefined) {
                            updatedScreen.stage.metadata.usePreprocessedInputs =
                                usePreprocessedInputs;
                        }

                        // Use the proper Redux action with the screen object
                        dispatch(
                            updateScreen({
                                id: screen.id,
                                screen: updatedScreen,
                            }),
                        );
                    }}
                />
            )}
        </div>
    );
}
